import { useMemo, useState } from "react";
import { Search, HeartPulse } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "./StatusBadge";
import { fmtDate } from "@/lib/utils2";

export default function DayHolidaysDialog({ date, requests, onClose, onOpenRequest }) {
  const [q, setQ] = useState("");
  const open = !!date;

  const sorted = useMemo(() => {
    const order = { approved: 0, pending: 1, rejected: 2, cancelled: 3 };
    return [...(requests || [])].sort((a, b) =>
      (order[a.status] ?? 9) - (order[b.status] ?? 9) ||
      a.last_name.localeCompare(b.last_name)
    );
  }, [requests]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter((r) =>
      `${r.first_name} ${r.last_name}`.toLowerCase().includes(needle) ||
      (r.email || "").toLowerCase().includes(needle) ||
      (r.department || "").toLowerCase().includes(needle) ||
      (r.job_role || "").toLowerCase().includes(needle) ||
      (r.supervisor_name || "").toLowerCase().includes(needle)
    );
  }, [sorted, q]);

  if (!date) return null;

  const heading = date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const counts = sorted.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="day-holidays-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{heading}</DialogTitle>
          <DialogDescription>
            {sorted.length === 0 ? "Nobody is off on this day." :
              <span className="flex gap-3 flex-wrap pt-1">
                <span><strong className="text-foreground">{sorted.length}</strong> contractor{sorted.length === 1 ? "" : "s"} off</span>
                {counts.approved && <span>· {counts.approved} approved</span>}
                {counts.pending && <span>· {counts.pending} pending</span>}
                {counts.rejected && <span>· {counts.rejected} rejected</span>}
                {counts.cancelled && <span>· {counts.cancelled} cancelled</span>}
              </span>
            }
          </DialogDescription>
        </DialogHeader>

        {sorted.length > 0 && (
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input
              data-testid="day-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, department, role or supervisor…"
              className="pl-9"
            />
          </div>
        )}

        {filtered.length > 0 ? (
          <div className="border border-border rounded-lg overflow-hidden bg-[#1E1E1E]">
            <Table data-testid="day-holidays-table">
              <TableHeader>
                <TableRow className="bg-[#141414]">
                  <TableHead>Contractor</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Supervisor</TableHead>
                  <TableHead className="whitespace-nowrap">Dates</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const isSick = (r.request_type || "").toLowerCase() === "sickness";
                  return (
                  <TableRow
                    key={r.id}
                    data-testid={`day-row-${r.id}`}
                    className={`cursor-pointer hover:bg-[#141414] ${isSick ? "bg-rose-950/20" : ""}`}
                    onClick={() => onOpenRequest?.(r)}
                  >
                    <TableCell className="font-medium">
                      {r.first_name} {r.last_name}
                      <div className="text-xs text-muted-foreground">{r.email}</div>
                    </TableCell>
                    <TableCell>{r.department}</TableCell>
                    <TableCell className="text-muted-foreground">{r.site || "—"}</TableCell>
                    <TableCell>{r.job_role}</TableCell>
                    <TableCell>
                      {isSick ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-rose-600 bg-rose-950/60 text-rose-200 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
                          <HeartPulse className="w-3 h-3"/> Sickness
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Absence</span>
                      )}
                    </TableCell>
                    <TableCell>{r.supervisor_name}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.days}</TableCell>
                    <TableCell><StatusBadge status={r.status}/></TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : sorted.length > 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-md">
            No results matching “{q}”.
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="day-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
